import { defineStore } from 'pinia'
import { http } from '@/extensions/http/index.js'
import { API_ENDPOINTS } from '@/config/index.js'

export const useNoticeStore = defineStore('notice', {
    state: () => {
        return {
            meta: null,

            noticesById: {},

            allIds: [],
            mineIds: [],
            unseenIds: [],
        }
    },
    getters: {
        all(state) {
            return state.allIds.map(id => state.noticesById[id]).filter(Boolean)
        },

        mine(state) {
            return state.mineIds.map(id => state.noticesById[id]).filter(Boolean)
        },

        unseen(state) {
            return state.unseenIds.map(id => state.noticesById[id]).filter(Boolean)
        },
    },
    actions: {
        normalizeNotices(notices) {
            for (const notice of notices) {
                this.noticesById[notice.id] = notice
            }
        },
        async init() {
            try {
                const { data } = await http.get(API_ENDPOINTS.NOTICE.INDEX.replace(':page', 1))

                this.normalizeNotices(data.data)

                this.allIds = data.data.map(notice => notice.id)
                this.meta = data.meta
            } catch { /**/ }
            try {
                const { data } = await http.get(API_ENDPOINTS.NOTICE.UNSEEN)

                const ids = data.map(notice => notice.id)

                this.unseenIds = ids
            } catch { /**/ }
            try {
                const { data } = await http.get(API_ENDPOINTS.NOTICE.MINE)

                const ids = data.map(notice => notice.id)

                this.mineIds = ids
            } catch { /**/ }
        },
        async fetchNextPage() {
            try {
                if (this.meta.last_page >= this.meta.current_page + 1) {
                    const { data } = await http.get(API_ENDPOINTS.NOTICE.INDEX.replace(':page', this.meta.current_page + 1))

                    if (data.data?.length) {
                        this.normalizeNotices(data.data)

                        const ids = data.data.map(notice => notice.id)

                        for (const id of ids) {
                            if (!this.allIds.includes(id)) {
                                this.allIds.push(id)
                            }
                        }

                        this.meta = data.meta
                    }
                }
            } catch { /**/ }
        },
        async storeComment(id, payload) {
            try {
                const { data } = await http.post(API_ENDPOINTS.NOTICE.COMMENT.STORE.replace(':id', id), payload)

                const notice = this.noticesById[id]
                if (!notice) return

                notice.comments.push(data)
            } catch { /**/ }
        },
        async like(id, payload) {
            try {
                const { data } = await http.post(API_ENDPOINTS.NOTICE.LIKE.replace(':id', id), payload)

                const notice = this.noticesById[id]
                if (!notice) return

                if (data.data) {
                    notice.likes.push(data.data)
                } else {
                    notice.likes = notice.likes.filter(like => like.user_id !== payload.user_id)
                }
            } catch { /**/ }
        },
        async uploadImages(id, { newFiles = [], existingIds = [] }) {
            try {
                const formData = new FormData()

                existingIds.forEach(existingId => {
                    formData.append('existing_images[]', existingId)
                })

                newFiles.forEach(file => {
                    formData.append('images[]', file)
                })

                const { data } = await http.post(
                    API_ENDPOINTS.NOTICE.UPLOAD_IMAGES.replace(':id', id),
                    formData
                )

                const notice = this.noticesById[id]
                if (notice) {
                    notice.images = data.data
                }
            } catch { /**/ }
        },
        async get(id) {
            try {
                if (this.noticesById[id]) return this.noticesById[id]

                const { data } = await http.get(API_ENDPOINTS.NOTICE.SHOW.replace(':id', id))
                return data
            } catch { /**/ }
        },
        async destroy(id) {
            try {
                await http.delete(API_ENDPOINTS.NOTICE.DESTROY.replace(':id', id))

                delete this.noticesById[id]

                this.allIds = this.allIds.filter(noticeId => noticeId !== id)
                this.mineIds = this.mineIds.filter(noticeId => noticeId !== id)
                this.unseenIds = this.unseenIds.filter(noticeId => noticeId !== id)
            } catch { /**/ }
        },
        async store(payload) {
            try {
                const { data } = await http.post(API_ENDPOINTS.NOTICE.STORE, payload)

                this.noticesById[data.id] = data

                this.allIds.unshift(data.id)
                this.mineIds.unshift(data.id)
                this.markSeen()

                return data.id
            } catch { /**/ }
        },
        async update(id, payload) {
            try {
                const { data } = await http.put(API_ENDPOINTS.NOTICE.UPDATE.replace(':id', id), payload)

                this.noticesById[id] = data

                return data.id
            } catch { /**/ }
        },
        async markSeen() {
            try {
                await http.post(API_ENDPOINTS.NOTICE.MARKSEEN)

                this.unseenIds = []
            } catch { /**/ }
        },
    }
})